// src/pages/league-dashboard/optimizer/OptimizerSegmented.js
// DD-RO-TOOLBAR-0728 — ONE segmented-control primitive for the optimizer toolbar.
//
// Why this exists: the toolbar rendered three different control treatments side by side —
//   • sub-view (Field View / Gap Analysis / Sequences): 4px/12px pad, fontSize 11, grouped
//   • hitters/pitchers:                                  4px/12px pad, fontSize 11, grouped
//   • mode (Balanced / Win Now / Dynasty):               4px/12px pad, fontSize 12, THREE separate
//                                                        bordered pills with a 6px gap (not a group)
//   • refresh:                                           4px/8px pad, own border
// Nothing set a height, so each control sized to its own text and the two font sizes produced
// visibly different heights; long labels ("Gap Analysis", "Pitchers") wrapped or clipped.
//
// The fix: every toolbar control is this component. Fixed CONTROL_HEIGHT, one font size/weight,
// cells joined by a shared 1px divider, nowrap labels. Colors come from dynastyTokens only.

import React from 'react';
import { dynastyTokens } from '../../../services/colorService';

const colors = dynastyTokens.colors;

// The single source of truth for toolbar control height. Any new toolbar control MUST use it —
// the drift this file fixes was caused by each control picking its own implicit height.
export const CONTROL_HEIGHT = 28;

const shellStyle = {
  display: 'inline-flex',
  height: CONTROL_HEIGHT,
  flexShrink: 0,
  border: `1px solid ${colors.neutral[700]}`,
  borderRadius: 4,
  background: colors.neutral[900],
  overflow: 'hidden',
};

const cellBase = {
  display: 'flex',
  alignItems: 'center',
  height: '100%',
  padding: '0 12px',
  fontSize: 12,
  fontWeight: 700,
  lineHeight: 1,
  whiteSpace: 'nowrap',
  border: 'none',
  background: 'transparent',
  color: colors.neutral[400],
  cursor: 'pointer',
  transition: 'background 0.15s, color 0.15s',
};

/**
 * options: [{ key, label, disabled?, title? }]
 * value:   the active key
 * onChange(key)
 */
export const OptimizerSegmented = ({ options, value, onChange, style }) => (
  <div style={{ ...shellStyle, ...style }} role="tablist">
    {options.map((opt, i) => {
      const active = opt.key === value;
      return (
        <button
          key={opt.key}
          type="button"
          role="tab"
          aria-selected={active}
          disabled={opt.disabled}
          title={opt.title || ''}
          onClick={() => !opt.disabled && onChange(opt.key)}
          style={{
            ...cellBase,
            borderLeft: i === 0 ? 'none' : `1px solid ${colors.neutral[700]}`,
            background: active ? colors.primary : 'transparent',
            color: active
              ? colors.neutral[950]
              : opt.disabled ? colors.neutral[600] : colors.neutral[400],
            cursor: opt.disabled ? 'default' : 'pointer',
          }}
          onMouseEnter={e => { if (!active && !opt.disabled) e.currentTarget.style.color = colors.neutral[200]; }}
          onMouseLeave={e => { if (!active && !opt.disabled) e.currentTarget.style.color = colors.neutral[400]; }}
        >
          {opt.label}
        </button>
      );
    })}
  </div>
);

/** Square icon button matched to the segmented height (refresh, etc.). */
export const OptimizerIconButton = ({ onClick, title, children, style }) => (
  <button
    type="button"
    onClick={onClick}
    title={title || ''}
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: CONTROL_HEIGHT,
      height: CONTROL_HEIGHT,
      flexShrink: 0,
      padding: 0,
      border: `1px solid ${colors.neutral[700]}`,
      borderRadius: 4,
      background: colors.neutral[900],
      color: colors.neutral[400],
      cursor: 'pointer',
      transition: 'color 0.15s, border-color 0.15s',
      ...style,
    }}
    onMouseEnter={e => {
      e.currentTarget.style.color = colors.primary;
      e.currentTarget.style.borderColor = colors.primary;
    }}
    onMouseLeave={e => {
      e.currentTarget.style.color = colors.neutral[400];
      e.currentTarget.style.borderColor = colors.neutral[700];
    }}
  >
    {children}
  </button>
);

/** Vertical hairline separating logical control groups in the toolbar. */
export const OptimizerToolbarDivider = () => (
  <div style={{ width: 1, height: 18, background: colors.neutral[700], flexShrink: 0, margin: '0 2px' }} />
);

export default OptimizerSegmented;
